import { useEffect, useSyncExternalStore } from 'react';
import { useMe } from './useMe';

/**
 * Theme preference. `system` is the default and follows the OS via
 * `prefers-color-scheme`; the other two pin the theme regardless of the OS.
 */
export type ThemePreference = 'light' | 'dark' | 'system';

/** What actually gets painted once `system` has been resolved against the OS. */
export type ResolvedTheme = 'light' | 'dark';

const DEFAULT: ThemePreference = 'system';

/* Must match the prefix in public/theme-boot.js. */
const PREFIX = 'payforgehr.theme.';

/* User-agnostic mirror of the last applied preference. theme-boot.js runs before /me resolves,
 * so it has no user id to key on and reads this instead. Kept in sync on every write. */
const LAST_KEY = PREFIX + 'last';

const DARK_QUERY = '(prefers-color-scheme: dark)';

function isThemePreference(value: unknown): value is ThemePreference {
  return value === 'light' || value === 'dark' || value === 'system';
}

/**
 * Keyed by the REAL user id, not the effective one, so an admin using "View as" does not
 * overwrite the impersonated user's stored theme (and vice versa). Same rule as
 * useCalendarSettings. Null until /me resolves.
 */
function storageKey(realUserId: number | undefined): string | null {
  return realUserId != null ? PREFIX + realUserId : null;
}

function read(key: string | null): ThemePreference {
  // Before /me resolves there is no per-user key yet. Fall back to the mirror so the picker
  // shows what theme-boot.js actually applied instead of flashing the default and back.
  try {
    const raw = localStorage.getItem(key ?? LAST_KEY);
    return isThemePreference(raw) ? raw : DEFAULT;
  } catch {
    // Private mode or storage disabled: the preference simply does not persist.
    return DEFAULT;
  }
}

function write(key: string | null, value: ThemePreference): void {
  try {
    if (key) localStorage.setItem(key, value);
    localStorage.setItem(LAST_KEY, value);
  } catch {
    /* Non-fatal: the theme still applies for this session, it just will not survive a reload. */
  }
}

function prefersDark(): boolean {
  return typeof window !== 'undefined' && typeof window.matchMedia === 'function'
    ? window.matchMedia(DARK_QUERY).matches
    : false;
}

/* --------------------------------------------------------------------------
 * Module-level store.
 *
 * Not component state: `useTheme` is consumed from two places at once (the app shell, which
 * exists to keep the effect alive whether or not any picker is mounted, and ThemePicker
 * itself). Two useState copies would diverge the moment one of them called setPreference, so
 * the state lives here and components subscribe via useSyncExternalStore. A React context
 * would also work; this avoids adding a provider to main.tsx for a single scalar.
 * -------------------------------------------------------------------------- */

type Snapshot = { preference: ThemePreference; resolved: ResolvedTheme };

let currentKey: string | null = null;
let preference: ThemePreference = read(null);
let systemDark = prefersDark();

function computeResolved(): ResolvedTheme {
  if (preference === 'system') return systemDark ? 'dark' : 'light';
  return preference;
}

/* Cached because useSyncExternalStore compares snapshots by identity - returning a fresh
 * object each call would loop forever. */
let snapshot: Snapshot = { preference, resolved: computeResolved() };

const listeners = new Set<() => void>();

/**
 * The single place that mutates the document. `.dark` is the selector every dark token in
 * index.css hangs off (`:root.dark`), and theme-boot.js sets the very same class pre-paint.
 */
function apply(resolved: ResolvedTheme): void {
  if (typeof document === 'undefined') return;
  document.documentElement.classList.toggle('dark', resolved === 'dark');
}

function commit(): void {
  const resolved = computeResolved();
  if (snapshot.preference === preference && snapshot.resolved === resolved) return;
  snapshot = { preference, resolved };
  apply(resolved);
  for (const listener of listeners) listener();
}

// Track the OS preference so a `system` user flips live when the OS changes it (macOS auto
// light/dark at sunset, for instance) without reloading the app.
if (typeof window !== 'undefined' && typeof window.matchMedia === 'function') {
  window.matchMedia(DARK_QUERY).addEventListener('change', (e) => {
    systemDark = e.matches;
    commit();
  });
}

/**
 * Adopt the per-user storage key once /me resolves. Idempotent - every consumer calls this
 * with the same key and only the first transition does work.
 */
function adoptKey(key: string | null): void {
  if (key === currentKey) return;
  currentKey = key;
  if (key === null) return;
  preference = read(key);
  commit();
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function getSnapshot(): Snapshot {
  return snapshot;
}

export function setThemePreference(next: ThemePreference): void {
  write(currentKey, next);
  preference = next;
  commit();
}

/** Test seam: resets the module store between specs. Not used by app code. */
export function __resetThemeStoreForTests(): void {
  currentKey = null;
  preference = read(null);
  systemDark = prefersDark();
  snapshot = { preference, resolved: computeResolved() };
}

/**
 * Three-state theme preference (Light / Dark / System), persisted per user in localStorage.
 *
 * Client-only by design: this is a per-device display choice, it matches the existing
 * localStorage prefs pattern in this app, and keeping it off the server avoids a migration
 * plus a change to the `/me` contract (which is deliberately byte-stable - see src/main/java/ai/payforge/hr/CLAUDE.md).
 */
export function useTheme() {
  const meQuery = useMe();
  const key = storageKey(meQuery.data?.realUserId);

  useEffect(() => {
    adoptKey(key);
  }, [key]);

  const { preference: pref, resolved } = useSyncExternalStore(subscribe, getSnapshot);

  return { preference: pref, resolved, setPreference: setThemePreference };
}
